
import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from "./utils";

interface HistoricalTrendsChartProps {
  data: any[];
  getFilterLabel: () => string;
}

const CustomizedDot = (props: any) => {
  const { cx, cy, stroke, payload, index } = props;
  const isLastDot = index === 2; // Assuming 3 data points
  const yoyValue = payload.yoy;
  const yoyColor = yoyValue > 0 ? "#4ade80" : "#ea384c";
  
  return (
    <g>
      <svg x={cx - 8} y={cy - 8} width={16} height={16} fill="url(#colorRevenue)" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="6" stroke={stroke} strokeWidth="2" />
      </svg>
      {payload.yoy !== null && (
        <text 
          x={cx} 
          y={cy - 20} 
          fill={yoyColor} 
          textAnchor={isLastDot ? "end" : "middle"} 
          fontSize="11" 
          fontWeight="bold"
        >
          {payload.yoy > 0 ? `+${payload.yoy}% YOY` : `${payload.yoy}% YOY`}
        </text>
      )}
    </g>
  );
};

export const HistoricalTrendsChart: React.FC<HistoricalTrendsChartProps> = ({ data, getFilterLabel }) => {
  return (
    <div className="bg-secondary/80 p-4 rounded-lg border border-primary/20">
      <h3 className="text-primary-light font-medium mb-2">
        Historical Revenue Trends
        <span className="text-purple-300 ml-1">{getFilterLabel()}</span>
      </h3>
      <p className="text-sm text-gray-400 mb-4">Compare forecast vs. actual over time periods to identify patterns.</p>
      
      <ResponsiveContainer width="100%" height={270}>
        <LineChart 
          data={data} 
          margin={{ top: 30, right: 30, left: 20, bottom: 15 }}
        >
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#D946EF" stopOpacity={0.3} />
            </linearGradient>
            <linearGradient id="colorBackground" x1="0" y1="0" x2="1" y2="0">
              <stop offset="5%" stopColor="#1A1F2C" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#304352" stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#555" opacity={0.4} />
          <XAxis 
            dataKey="year" 
            stroke="#9b87f5" 
            tick={{ fill: '#9b87f5', fontSize: 11 }} 
            axisLine={{ stroke: '#444' }} 
          />
          <YAxis 
            stroke="#9b87f5" 
            tickFormatter={value => formatCurrency(value)} 
            tick={{ fill: '#9b87f5', fontSize: 11 }} 
            axisLine={{ stroke: '#444' }} 
          />
          <Tooltip 
            formatter={(value: number) => [formatCurrency(value), "Revenue"]} 
            contentStyle={{
              backgroundColor: '#1A1F2C',
              borderColor: '#9b87f5',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              color: '#fff'
            }} 
            labelStyle={{
              color: '#D6BCFA',
              fontWeight: 'bold',
              marginBottom: '5px'
            }} 
          />
          <Legend 
            wrapperStyle={{
              color: '#F2FCE2',
              fontSize: 12,
              paddingTop: '10px'
            }} 
            iconType="circle" 
            iconSize={10} 
          />
          <Line 
            type="monotone" 
            dataKey="revenue" 
            name="Annual Revenue" 
            stroke="url(#colorRevenue)" 
            strokeWidth={3} 
            fillOpacity={1} 
            fill="url(#colorRevenue)" 
            activeDot={{
              r: 8,
              fill: '#8B5CF6',
              strokeWidth: 2,
              stroke: '#fff'
            }} 
            dot={CustomizedDot} 
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
